import { useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import { useAuthContext } from "providers/Auth";
import { Dispatch, SetStateAction, useState } from "react";
import { AdjustedApplicationRentActivity } from "shared/app/ActivityLog/types";
import { Card } from "shared/components/Card";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { CriteriaRentType } from "shared/types";
import { ApplicationStatus, Money } from "shared/types/application";
import { formatToCurrency } from "shared/utils";
import { Property, Unit } from "types/landlord/address";
import { ApplicationRentSection } from "views/applications-id/components/LayoutHeader/components/ApplicationRentSection";
import { Stat } from "./components/Stat";
import { ScreeningStatusBadge } from "shared/app/ApplicationsTableView/components/ScreeningStatusBadge";
import { ApplicationStatusBadge } from "shared/app/ApplicationStatusBadge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "shared/components/Tooltip";
import { HugeiconsIcon, IconSvgElement } from "@hugeicons/react";
import { UnavailableIcon } from "@hugeicons-pro/core-stroke-rounded";
import { ChangeDesiredMoveInDateModal } from "./components/ChangeDesiredMoveInDateModal";
import { Show, Button, ButtonSize, ButtonVariant } from "shared/components";
import { ChangeDesiredLeaseTermModal } from "./components/ChangeDesiredLeaseTermModal";

export const ApplicationOverview = ({
  applicationId,
  applicationRentAmount,
  setShowActivityLog,
  applicationStatus,
  latestApplicationRentHistory,
  unit,
  property,
  desiredMoveInDate,
  desiredLeaseTerm,
  leaseTermEnabled,
  screeningStatus,
  criteriaRentType,
  unitDelisted,
  refetchApplication
}: {
  applicationId: string;
  applicationRentAmount: {
    monthly_rent_amount?: Money;
    rent_amount?: Money;
    home_rent_amount?: Money;
    site_rent_amount?: Money;
    previous_rent_amount?: Money;
    housing_assistance_voucher_amount?: Money;
    pet_rent_amount?: Money;
  };
  setShowActivityLog: Dispatch<SetStateAction<boolean>>;
  applicationStatus: ApplicationStatus;
  latestApplicationRentHistory?: AdjustedApplicationRentActivity;
  unit: Unit;
  property: Property;
  desiredMoveInDate?: string;
  desiredLeaseTerm?: number;
  leaseTermEnabled: boolean;
  screeningStatus?: string;
  criteriaRentType: CriteriaRentType;
  unitDelisted?: boolean;
  refetchApplication: () => void;
}) => {
  const dictionary = useUnitsPropertiesDictionary();
  const { permissions, pms, type } = useAuthContext();
  const homeSiteRentEnabled = pms.isManageAmerica || type.isHomeSiteRentEnabled;
  const queryClient = useQueryClient();

  const [openedDesiredMoveInDateModal, setOpenedDesiredMoveInDateModal] =
    useState(false);
  const [openedDesiredLeaseTermModal, setOpenedDesiredLeaseTermModal] =
    useState(false);

  const desiredLeaseTermMonths = desiredLeaseTerm === 1 ? "month" : "months";
  const desiredLeaseTermLabel = desiredLeaseTerm
    ? `${desiredLeaseTerm} ${desiredLeaseTermMonths}`
    : "--";

  return (
    <Card className="w-full">
      <div className="border-b border-alpha-40 px-6 py-4">
        <ApplicationRentSection
          applicationId={applicationId}
          applicationRentAmount={applicationRentAmount}
          onViewDetails={() => setShowActivityLog(true)}
          canEdit={
            permissions.canEditApplicationRent &&
            [
              ApplicationStatus.Pending,
              ApplicationStatus.Started,
              ApplicationStatus.PartiallySubmitted,
              ApplicationStatus.Submitted
            ].includes(applicationStatus)
          }
          onSuccess={() => {
            queryClient.invalidateQueries({
              queryKey: ["APPLICATION", applicationId]
            });
          }}
          latestApplicationRentHistory={latestApplicationRentHistory}
          criteriaRentType={criteriaRentType}
        />
      </div>

      <div className="w-full [@media(min-width:1695px)]:flex block">
        {/* Property Information Section */}
        <div className="flex items-center gap-6 p-6 flex-wrap">
          <Stat
            label="Application status"
            value={
              <ApplicationStatusBadge
                type="application"
                status={applicationStatus}
                className="text-xs font-semibold"
              />
            }
          />
          <Stat
            label="Screening status"
            value={
              screeningStatus ? (
                <ScreeningStatusBadge
                  status={screeningStatus}
                  className="text-xs font-semibold"
                />
              ) : (
                "--"
              )
            }
          />

          <Stat
            label={`${dictionary.Property} name`}
            value={property.name || "--"}
          />
          <Stat
            label={`${dictionary.Property}`}
            value={`${property.address.address1}` || "--"}
          />
          <Stat
            label={`${dictionary.Unit}`}
            value={
              unitDelisted ? (
                <div className="flex items-start gap-2">
                  <Tooltip placement="top">
                    <TooltipTrigger>
                      <div className="bg-danger-50 flex items-center justify-center p-1 rounded-2xl shrink-0">
                        <HugeiconsIcon
                          icon={UnavailableIcon as IconSvgElement}
                          size={12}
                          className="text-danger-500"
                        />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent className="bg-alpha-800 rounded-lg border p-4 text-sm text-white max-w-[250px]">
                      Applications for this {dictionary.Unit.toLowerCase()} are
                      no longer allowed
                    </TooltipContent>
                  </Tooltip>
                  <p className="text-sm font-medium text-content-1000">
                    {unit.title || "-"}
                  </p>
                </div>
              ) : (
                `${unit.title || "--"}`
              )
            }
          />
          <Stat
            label={`${dictionary.Property} group`}
            value={property.property_group?.name || "--"}
          />
        </div>

        <div className="flex flex-wrap">
          {/* Rent Information Section */}
          <div className="flex items-center gap-6 p-6">
            {!homeSiteRentEnabled ? (
              <Stat
                label="Listed rent"
                value={formatToCurrency({
                  cents: unit.monthly_rent_amount?.cents || 0,
                  currency: "USD"
                })}
              />
            ) : (
              <>
                <Stat
                  label="Listed site rent"
                  value={formatToCurrency({
                    cents: unit.site_rent_amount?.cents || 0,
                    currency: "USD"
                  })}
                />

                <Stat
                  label="Listed home rent"
                  value={formatToCurrency({
                    cents: unit.home_rent_amount?.cents || 0,
                    currency: "USD"
                  })}
                />
              </>
            )}
          </div>

          {/* Date Information Section */}
          <div className="flex items-center gap-6 p-6">
            <Stat
              label="Available"
              value={
                unit.available_at && moment(unit.available_at).isValid()
                  ? moment(unit.available_at).utc().format("MMM D, YYYY")
                  : "--"
              }
            />
            <Stat
              label="Desired move-in"
              value={
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-content-1000">
                    {desiredMoveInDate && moment(desiredMoveInDate).isValid()
                      ? moment(desiredMoveInDate).format("MMM D, YYYY")
                      : "--"}
                  </p>
                  <Show if={permissions.canChangeDesiredMoveInDate}>
                    <Button
                      variant={ButtonVariant.TEXT}
                      size={ButtonSize.SM}
                      onClick={() => setOpenedDesiredMoveInDateModal(true)}
                    >
                      Edit
                    </Button>
                  </Show>
                </div>
              }
            />
            <Show if={leaseTermEnabled}>
              <Stat
                label="Desired lease term"
                value={
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-content-1000">
                      {desiredLeaseTermLabel}
                    </p>
                    <Button
                      variant={ButtonVariant.TEXT}
                      size={ButtonSize.SM}
                      onClick={() => setOpenedDesiredLeaseTermModal(true)}
                    >
                      Edit
                    </Button>
                  </div>
                }
              />
            </Show>
          </div>
        </div>
      </div>
      <Show if={permissions.canChangeDesiredMoveInDate}>
        <ChangeDesiredMoveInDateModal
          opened={openedDesiredMoveInDateModal}
          onClose={() => setOpenedDesiredMoveInDateModal(false)}
          applicationId={applicationId}
          desiredMoveInDate={desiredMoveInDate || ""}
          refetchApplication={refetchApplication}
        />
      </Show>
      <ChangeDesiredLeaseTermModal
        opened={openedDesiredLeaseTermModal}
        onClose={() => setOpenedDesiredLeaseTermModal(false)}
        applicationId={applicationId}
        desiredLeaseTerm={desiredLeaseTerm || 0}
        refetchApplication={refetchApplication}
      />
    </Card>
  );
};
